import { Timestamp, timestampFromDate } from '@bufbuild/protobuf/wkt';
import {
  ListActionsAndIssuesGraphRequest_Target,
  ListMetricsRequest_AggregationType,
  ListMetricsRequest_SortOrder,
  ListMetricsRequest_Target,
  ListMetricsResponse,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import {
  Device,
  Product,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import {
  ListActionsGraphRequest_Target,
  ListSimulationMetricsRequest_AggregationType,
  ListSimulationMetricsRequest_SortOrder,
  ListSimulationMetricsRequest_Target,
} from '@wavingroup/aqora-v2-crate/wavin/simulations/v1/simulations_service_pb';
import { Dayjs } from 'dayjs';
import { aqoraClient } from '~/services/api/aqora-client';
import { simulationsClient } from '~/services/api/simulations-client';
import { authService } from '~/services/auth/auth-service';
import { TRANSPORT_URL } from '~/services/environment/environment';

const getLastWeekRange = () => {
  const now = new Date();
  const pastWeek = new Date(now.setDate(now.getDate() - 7));

  return {
    rangeFrom: timestampFromDate(pastWeek),
    rangeTo: timestampFromDate(new Date()),
  };
};

export const listMetrics = async ({
  targets,
  aggregation = ListMetricsRequest_AggregationType.UNSPECIFIED,
  rangeFrom = getLastWeekRange().rangeFrom,
  rangeTo = getLastWeekRange().rangeTo,
}: {
  targets: ListMetricsRequest_Target[];
  aggregation?: ListMetricsRequest_AggregationType;
  rangeFrom?: Timestamp;
  rangeTo?: Timestamp;
}): Promise<ListMetricsResponse> =>
  await aqoraClient.listMetrics({
    targets,
    sortOrder: ListMetricsRequest_SortOrder.ASC,
    aggregation,
    rangeFrom,
    rangeTo,
  });

export const listSimulationMetrics = async ({
  simulationId,
  targets,
  aggregation = ListSimulationMetricsRequest_AggregationType.UNSPECIFIED,
  rangeFrom,
  rangeTo,
}: {
  targets: ListSimulationMetricsRequest_Target[];
  aggregation?: ListSimulationMetricsRequest_AggregationType;
  rangeFrom: Timestamp;
  rangeTo: Timestamp;
  simulationId: string;
}) =>
  simulationsClient.listSimulationMetrics({
    runAttemptName: simulationId,
    targets,
    aggregation,
    rangeFrom,
    rangeTo,
    sortOrder: ListSimulationMetricsRequest_SortOrder.ASC,
  });

export type DownloadMetricsBody = {
  resource_names: Device['name'][];
  from: string;
  to: string;
};

function getFilenameFromContentDisposition(contentDisposition: string | null) {
  if (!contentDisposition) return null;
  const match = contentDisposition.match(
    /filename\*?=['"]?(?:UTF-8['"]*)?([^;\r\n"]+)/i,
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export const downloadMetricsFile = async ({
  productNames,
  from,
  to,
}: {
  productNames: Product['name'][];
  from: Dayjs;
  to: Dayjs;
}): Promise<{
  data: Blob;
  filename: string | null;
}> => {
  const token = await authService.getIdentityToken();

  const requestBody = {
    resource_names: productNames,
    from: from.tz().format(),
    to: to.tz().format(),
  } satisfies DownloadMetricsBody;

  const response = await fetch(`${TRANSPORT_URL}/download/metrics`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok && response.status === 404) {
    throw new Error('No metrics found for the given time range');
  }

  if (!response.ok) {
    throw new Error('Error while fetching the metric file');
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');

  const filename = getFilenameFromContentDisposition(contentDisposition);

  return {
    data: blob,
    filename,
  };
};

export const listActionsAndIssuesGraph = async ({
  targets,
  rangeFrom,
  rangeTo,
}: {
  targets: ListActionsAndIssuesGraphRequest_Target[];
  rangeFrom: Timestamp;
  rangeTo: Timestamp;
}) =>
  aqoraClient.listActionsAndIssuesGraph({
    targets,
    rangeFrom,
    rangeTo,
  });

export const listSimulationActionsGraph = async ({
  targets,
  simulationId,
  rangeFrom,
  rangeTo,
}: {
  targets: ListActionsGraphRequest_Target[];
  simulationId: string;
  rangeFrom: Timestamp;
  rangeTo: Timestamp;
}) =>
  simulationsClient.listActionsGraph({
    targets,
    runAttemptName: simulationId,
    rangeFrom,
    rangeTo,
  });
